import {
  createStylesParams,
  IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import {
  StyleType,
  wixColor,
  wixFont,
  CssColor,
  CssFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import { StylesParamsKeys } from './common/settingsAndStylesEnums';

export type IStylesParams = {
  [StylesParamsKeys.productTitleColor]: StyleParamType.Color;
  [StylesParamsKeys.productTitleFont]: StyleParamType.Font;
  [StylesParamsKeys.productPriceColor]: StyleParamType.Color;
  [StylesParamsKeys.productPriceFont]: StyleParamType.Font;
  [StylesParamsKeys.productDescriptionColor]: StyleParamType.Color;
  [StylesParamsKeys.productDescriptionFont]: StyleParamType.Font;
  [StylesParamsKeys.backgroundColor]: StyleParamType.Color;
  [StylesParamsKeys.imageBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.imageBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.imageCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.imageRatio]: StyleParamType.Number;
  [StylesParamsKeys.inputFieldBackgroundColor]: StyleParamType.Color;
  [StylesParamsKeys.inputFieldBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.inputFieldBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.inputFieldFont]: StyleParamType.Font;
  [StylesParamsKeys.inputFieldColor]: StyleParamType.Color;
  [StylesParamsKeys.inputFieldTitlesColor]: StyleParamType.Color;
  [StylesParamsKeys.inputFieldTitlesFont]: StyleParamType.Font;
  [StylesParamsKeys.selectionButtonsCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.inputFieldsCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.ProductVariantsFillColorOpacity]: StyleParamType.Color;
  [StylesParamsKeys.ProductVariantsBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.ProductVariantsBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.ProductVariantsSelectedBoxColor]: StyleParamType.Color;
  [StylesParamsKeys.ProductVariantsTextColor]: StyleParamType.Color;
  [StylesParamsKeys.ProductVariantsTextFont]: StyleParamType.Font;
  [StylesParamsKeys.AmountSelectorOptionFillColorOpacity]: StyleParamType.Color;
  [StylesParamsKeys.AmountSelectorOptionSelectedBoxColor]: StyleParamType.Color;
  [StylesParamsKeys.AmountSelectorOptionBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.AmountSelectorOptionBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.AmountSelectorOptionTextColor]: StyleParamType.Color;
  [StylesParamsKeys.AmountSelectorOptionTextFont]: StyleParamType.Font;
  [StylesParamsKeys.AmountSelectorOptionCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.GiftCardRecipientFillColorOpacity]: StyleParamType.Color;
  [StylesParamsKeys.GiftCardRecipientTextColor]: StyleParamType.Color;
  [StylesParamsKeys.GiftCardRecipientTextFont]: StyleParamType.Font;
  [StylesParamsKeys.GiftCardRecipientSelectedBoxColor]: StyleParamType.Color;
  [StylesParamsKeys.GiftCardRecipientBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.GiftCardRecipientBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.GiftCardRecipientCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.BuyNowButtonTextFont]: StyleParamType.Font;
  [StylesParamsKeys.BuyNowButtonTextColor]: StyleParamType.Color;
  [StylesParamsKeys.BuyNowButtonCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.BuyNowButtonBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.BuyNowButtonBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.BuyNowButtonFillColorOpacity]: StyleParamType.Color;
  [StylesParamsKeys.AddToCartButtonTextFont]: StyleParamType.Font;
  [StylesParamsKeys.AddToCartButtonTextColor]: StyleParamType.Color;
  [StylesParamsKeys.AddToCartButtonCornerRadius]: StyleParamType.Number;
  [StylesParamsKeys.AddToCartButtonBorderColor]: StyleParamType.Color;
  [StylesParamsKeys.AddToCartButtonBorderWidth]: StyleParamType.Number;
  [StylesParamsKeys.AddToCartButtonFillColorOpacity]: StyleParamType.Color;
  [StylesParamsKeys.shouldShowDeliveryDate]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowBuyNowButton]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowAddToCartButton]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowTitle]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowDescription]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowAmountOptions]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowQuantitySelector]: StyleParamType.Boolean;
  [StylesParamsKeys.shouldShowImage]: StyleParamType.Boolean;
};

const booleanStylesParam = ({
  defaultValue = true,
}: {
  defaultValue?: boolean;
} = {}): IStyleParam<StyleParamType.Boolean> => ({
  type: StyleParamType.Boolean,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  },
});

const stylesParams = createStylesParams<IStylesParams>({
  [StylesParamsKeys.productTitleColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstHeading2Color),
    },
  },
  [StylesParamsKeys.productTitleFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading2Font),
    },
  },
  [StylesParamsKeys.productPriceColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.productPriceFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-S'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading6Font),
    },
  },
  [StylesParamsKeys.productDescriptionColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.productDescriptionFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-L'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph1Font),
    },
  },
  [StylesParamsKeys.backgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor, 0),
      ai: {
        description: 'The background color for the gift card page.',
      },
    },
  },
  [StylesParamsKeys.imageBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-2'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstSecondaryBackgroundColor),
    },
  },
  [StylesParamsKeys.imageBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.imageCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StylesParamsKeys.imageRatio]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 3 / 2,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 3 / 2,
      },
    },
  },
  [StylesParamsKeys.inputFieldBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.inputFieldBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  [StylesParamsKeys.inputFieldBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.inputFieldFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.inputFieldColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.inputFieldTitlesColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.inputFieldTitlesFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.selectionButtonsCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.inputFieldsCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.ProductVariantsFillColorOpacity]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.ProductVariantsBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.ProductVariantsBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.ProductVariantsSelectedBoxColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StylesParamsKeys.ProductVariantsTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.ProductVariantsTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionFillColorOpacity]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.ProductVariantsFillColorOpacity,
      );
      return value;
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionSelectedBoxColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.ProductVariantsSelectedBoxColor,
      );
      return value;
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsBorderColor);
      return value;
    },
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsBorderWidth);
      return value;
    },
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.AmountSelectorOptionTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsTextColor);
      return value;
    },
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsTextFont);
      return value;
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.AmountSelectorOptionCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.selectionButtonsCornerRadius,
      );
      return value;
    },
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.GiftCardRecipientFillColorOpacity]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.ProductVariantsFillColorOpacity,
      );
      return value;
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.GiftCardRecipientTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsTextColor);
      return value;
    },
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.GiftCardRecipientTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsTextFont);
      return value;
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.GiftCardRecipientSelectedBoxColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.ProductVariantsSelectedBoxColor,
      );
      return value;
    },
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StylesParamsKeys.GiftCardRecipientBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsBorderColor);
      return value;
    },
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  [StylesParamsKeys.GiftCardRecipientBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(stylesParams.ProductVariantsBorderWidth);
      return value;
    },
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.GiftCardRecipientCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      const value = getStyleParamValue(
        stylesParams.selectionButtonsCornerRadius,
      );
      return value;
    },
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.BuyNowButtonTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.BuyNowButtonTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.BuyNowButtonCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.BuyNowButtonBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.BuyNowButtonBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.BuyNowButtonFillColorOpacity]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },

  [StylesParamsKeys.AddToCartButtonTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  [StylesParamsKeys.AddToCartButtonTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.AddToCartButtonCornerRadius]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.AddToCartButtonBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  [StylesParamsKeys.AddToCartButtonBorderWidth]: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  [StylesParamsKeys.AddToCartButtonFillColorOpacity]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  [StylesParamsKeys.shouldShowDeliveryDate]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowBuyNowButton]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowAddToCartButton]: booleanStylesParam({
    defaultValue: false,
  }),
  [StylesParamsKeys.shouldShowTitle]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowDescription]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowAmountOptions]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowQuantitySelector]: booleanStylesParam(),
  [StylesParamsKeys.shouldShowImage]: booleanStylesParam(),
});

export default stylesParams;
